import React, { useEffect, useState } from 'react';
import { getAuth, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
// eslint-disable-next-line no-restricted-imports -- direct Firestore access, not yet migrated to a service (see ARCHITECTURE_AUDIT.md)
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, app } from '../../firebase';
import PhoneInput, { formatE164ForDisplay } from '../../components/PhoneInput';
import './Profile.css';

const auth = getAuth(app);

export default function Profile({ email, onClose }) {
  const [userData, setUserData] = useState({ name: '', email: '', phoneNumber: '', role: '' });
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhoneNumber, setEditedPhoneNumber] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        setEditedName(data.name || '');
        setEditedEmail(data.email || '');
        setEditedPhoneNumber(data.phoneNumber || '');
      }
    };

    fetchData();
  }, []);

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const docRef = doc(db, 'users', user.uid);
      const updateData = {
        name: editedName,
      };

      if (editedEmail) {
        updateData.email = editedEmail.toLowerCase();
      }

      if (editedPhoneNumber) {
        // PhoneInput already formats it, just use the value
        updateData.phoneNumber = editedPhoneNumber.trim();
      }

      await updateDoc(docRef, updateData);

      // Only update email in Firebase Auth if it changed and exists
      if (editedEmail && editedEmail !== user.email) {
        try {
          await updateEmail(user, editedEmail);
        } catch (emailError) {
          console.warn('Could not update email in Firebase Auth:', emailError);
          // Continue anyway - Firestore is updated
        }
      }

      setUserData({
        ...userData,
        name: editedName,
        email: editedEmail,
        phoneNumber: updateData.phoneNumber || editedPhoneNumber,
      });
      setEditMode(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please re-authenticate or try again later.');
    }
  };

  const handleCancel = () => {
    setEditedName(userData.name);
    setEditedEmail(userData.email);
    setEditedPhoneNumber(userData.phoneNumber);
    setEditMode(false);
    setShowPasswordChange(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handlePasswordChange = async () => {
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    const user = auth.currentUser;
    if (!user || !user.email) {
      setPasswordError('User not authenticated. Please log in again.');
      return;
    }

    try {
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      alert('Password changed successfully!');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect.');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('New password is too weak. Please use a stronger password.');
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-topbar">
        <button className="profile-back-btn" onClick={onClose}>
          ← Back
        </button>
        <h2 className="profile-title">My Profile</h2>
      </div>

      <div className="profile-card">
        <div className="profile-grid">
          <div className="profile-label">Name</div>
          <div className="profile-value">
            {editMode ? (
              <input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                style={{ width: '100%' }}
              />
            ) : (
              userData.name || <span className="profile-muted">Not set</span>
            )}
          </div>

          <div className="profile-label">Phone Number</div>
          <div className="profile-value">
            {editMode ? (
              <PhoneInput
                value={editedPhoneNumber}
                onChange={(e) => setEditedPhoneNumber(e.target.value)}
              />
            ) : userData.phoneNumber ? (
              formatE164ForDisplay(userData.phoneNumber)
            ) : (
              <span className="profile-muted">Not set</span>
            )}
          </div>

          <div className="profile-label">Email</div>
          <div className="profile-value">
            {editMode ? (
              <input
                type="email"
                value={editedEmail}
                onChange={(e) => setEditedEmail(e.target.value)}
                placeholder="Optional"
                style={{ width: '100%' }}
              />
            ) : (
              userData.email || <span className="profile-muted">Not set</span>
            )}
          </div>

          <div className="profile-label">Access Level</div>
          <div className="profile-value">
            {(userData.role ? userData.role.charAt(0).toUpperCase() + userData.role.slice(1) : 'Worker')}
          </div>

          <div className="profile-label">Password</div>
          <div className="profile-value">
            {!showPasswordChange ? (
              <>
                ********
                <button
                  className="profile-action-btn"
                  style={{ marginLeft: 10 }}
                  onClick={() => setShowPasswordChange(true)}
                >
                  Change Password
                </button>
              </>
            ) : null}
          </div>
        </div>

        {showPasswordChange && (
          <div className="profile-password-box">
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div className="profile-label">Current Password *</div>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <div className="profile-label">New Password *</div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <div className="profile-label">Confirm New Password *</div>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                />
              </div>
              {passwordError && <div style={{ color: '#dc2626', fontSize: 12 }}>{passwordError}</div>}
              <div className="profile-actions">
                <button className="profile-action-btn primary" onClick={handlePasswordChange}>
                  Save Password
                </button>
                <button
                  className="profile-action-btn"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="profile-actions">
          {!editMode ? (
            <button className="profile-action-btn primary" onClick={() => setEditMode(true)}>
              Edit Profile
            </button>
          ) : (
            <>
              <button className="profile-action-btn primary" onClick={handleSave}>
                Save Changes
              </button>
              <button className="profile-action-btn" onClick={handleCancel}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
